'use client';

import { captureClientException } from '@instamenu/posthog/client';
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { StripeAccountsQuery, useStripeAccountsQuery } from './queries.generated';

export type StripeContextValue = {
  accounts?: StripeAccountsQuery['stripeAccounts'];
  refreshAccounts: () => void;
} & (
  | {
      dueAccounts?: StripeAccountsQuery['stripeAccounts'];
      showRequirementsBanner: false;
    }
  | {
      dueAccounts: StripeAccountsQuery['stripeAccounts'];
      showRequirementsBanner: true;
    }
);

type StripeRequirements = {
  currently_due: string[];
  current_deadline: string | null;
};

export const StripeContext = createContext<StripeContextValue | undefined>(undefined);

export const StripeProvider = ({ children }: PropsWithChildren) => {
  const [{ data, error, fetching }, reexecuteAccountsQuery] = useStripeAccountsQuery({
    pause: true,
  });
  const initialRequestStarted = useRef(false);
  const reportedError = useRef<typeof error>(undefined);

  useEffect(() => {
    if (initialRequestStarted.current) return;

    initialRequestStarted.current = true;
    reexecuteAccountsQuery({ requestPolicy: 'network-only' });
  }, [reexecuteAccountsQuery]);

  useEffect(() => {
    if (fetching) return;

    if (error) {
      if (reportedError.current === error) return;

      reportedError.current = error;
      captureClientException(error, {
        source: 'stripe.context.fetch-accounts',
      });
    } else if (data && !data.stripeAccounts) {
      reportedError.current = undefined;
      captureClientException(new Error('stripeAccounts not found'), {
        source: 'stripe.context.fetch-accounts',
      });
    } else {
      reportedError.current = undefined;
    }
  }, [data, error, fetching]);

  const refreshAccounts = useCallback(() => {
    reexecuteAccountsQuery({ requestPolicy: 'network-only' });
  }, [reexecuteAccountsQuery]);

  const accounts = data?.stripeAccounts;

  const dueAccounts = accounts?.filter(
    (account) => (account.requirements as StripeRequirements).currently_due.length > 0,
  );

  const showRequirementsBanner =
    !process.env.NEXT_PUBLIC_DISABLE_STRIPE_REQUIREMENTS_BANNER && !!dueAccounts?.length;

  return (
    <StripeContext.Provider
      value={
        // this allows the typescript compiler to infer the correct type of the discriminated union
        showRequirementsBanner
          ? { accounts, dueAccounts, refreshAccounts, showRequirementsBanner }
          : { accounts, dueAccounts, refreshAccounts, showRequirementsBanner }
      }
    >
      {children}
    </StripeContext.Provider>
  );
};

export function useStripeContext() {
  const ctx = useContext(StripeContext);
  if (!ctx) {
    throw new Error('useStripeContext must be used within a StripeProvider');
  }
  return ctx;
}
